import * as React from 'react';
import {
	MagazineArticleItemCard,
	MagazineArticleItemSmallCard,
	ArticleItemCard,
	SimilarArticleDesktopItemCard,
	AdminArticleItemCard,
	UserItemCard,
	AdminPollItemCard,
	FollowerFollowingItemCard,
	AdminTagItemCard,
	ProductItemCard,
	WardrobeItemCard,
	OutfitItemCard,
	OrderItemCard,
	OrderCardItemCard,
	SelectorItemCard,
	ShopDashboardOrderItemCard,
	ShopDashboardProductItemCard,
	AdminShopReviewItemCard,
	StaffPreviewItemCard,
	NotificationItemCard,
	UserShippingAddressItemCard,
	UserPaymentMethodItemCard,
	OnboardingQuizFollowItemCard,
	UserRecentActivityItemCard,
	OutfitItemSmallItemCard,
	CommentItemCard,
	ShopItemCard,
	ShoppingBagProductItemCard,
	CheckoutProductItemCard,
	AdminMenuItemCard,
	AdminShopItemCard,
	PlaceholderItemCard,
	OfficeItemCard,
	CustomerItemCard,
} from 'components/ItemList/ItemCards';
import { IItemCardList } from 'models/itemcard/itemcardlist';
import * as i18n from 'i18next';
import * as c from 'models/constants';
import { Spinner, Button } from '../components';
import { IDomainValue, IPage, IEntityId, ITranslateProps, ILanguage } from 'models/common';
import { getShopCard } from 'redux/modules/shop/shopcard';
import { getProductCard } from 'redux/modules/product/productcard';
import { getWardrobeItemCard } from 'redux/modules/wardrobe/wardrobeitemcard';
import { getUserCard } from 'redux/modules/user/usercard';
import { getArticleCard } from 'redux/modules/magazine/article/articlecard';
import { getOutfitCard } from 'redux/modules/outfit/outfitcard';
import { ProductRefinementGroupings } from 'models';
import { MenuItemType } from '../models/enums/menuitemtype';
import * as moment from 'moment';
import { ReactDatePickerProps } from 'react-datepicker';
import { IModalAction } from 'models/modal';
import { momentWithTimezone } from 'redux/modules/shop/helpers';

const cardsLookupTable = {
	ArticleItemCard,
	AdminShopItemCard,
	AdminArticleItemCard,
	AdminPollItemCard,
	AdminTagItemCard,
	CheckoutProductItemCard,
	CommentItemCard,
	FollowerFollowingItemCard,
	OutfitItemCard,
	OutfitItemSmallItemCard,
	MagazineArticleItemCard,
	MagazineArticleItemSmallCard,
	OrderItemCard,
	OrderCardItemCard,
	ShopItemCard,
	SelectorItemCard,
	ShopDashboardOrderItemCard,
	ShopDashboardProductItemCard,
	OnboardingQuizFollowItemCard,
	ProductItemCard,
	ShoppingBagProductItemCard,
	SimilarArticleDesktopItemCard,
	StaffPreviewItemCard,
	UserItemCard,
	UserPaymentMethodItemCard,
	UserShippingAddressItemCard,
	UserRecentActivityItemCard,
	WardrobeItemCard,
	AdminShopReviewItemCard,
	NotificationItemCard,
	AdminMenuItemCard,
	OfficeItemCard,
	CustomerItemCard,
};

type CardType = keyof typeof cardsLookupTable;

export function getCardItems(data: IItemCardList): JSX.Element[] {
	let list = [];
	if (data.items !== null) {
		list = data.items;
	}

	let itemCards: JSX.Element[] = [];
	itemCards = list?.map((item) => {
		return getItemCard(item, data);
	});

	return itemCards;
}

function getItemCard(item: any, data: IItemCardList): JSX.Element {
	let componentContent = null;
	let id = null;
	let key = null;
	if (item.id) {
		id = item.id;
		key = item.id;
	} else if (item.key) {
		key = item.key;
	} else if (item.entityId) {
		const parsedEntityId = JSON.parse(item.entityId);
		key = parsedEntityId.type + '_' + parsedEntityId.id;
	}

	if (!id && item.entityId) {
		const parsedEntityId = JSON.parse(item.entityId);
		id = parsedEntityId.id;
	}

	let type = null;
	if (item.type) {
		type = item.type;
	} else if (item.entityId) {
		const parsedEntityId = JSON.parse(item.entityId);
		type = parsedEntityId.type;
	}

	const props = Object.assign({ item }, data);
	if (data.followItem) {
		props.followItem = () => data.followItem(type, id);
	}
	if (data.unfollowItem) {
		props.unfollowItem = () => data.unfollowItem(type, id);
	}
	if (data.likeItem) {
		props.likeItem = () => data.likeItem(type, id);
	}
	if (data.unlikeItem) {
		props.unlikeItem = () => data.unlikeItem(type, id);
	}
	if (data.onMouseEnter) {
		props.onMouseEnter = (e?: React.MouseEvent) => data.onMouseEnter(item, e);
	}
	if (data.onMouseLeave) {
		props.onMouseLeave = (e?: React.MouseEvent) => data.onMouseLeave(item, e);
	}
	if (data.followingItems && item.entityId) {
		const followingItem = data.followingItems.find((i) => i.entityId === item.entityId);
		props.isFollowing = followingItem ? followingItem.following : undefined;
	}
	if (data.itemClicked) {
		props.itemClicked = data.itemClicked;
	}

	const itemType = data.itemType ? data.itemType : item.itemType;
	const Component = cardsLookupTable[itemType];
	if (!Component) {
		throw new Error('Unsupported card type: ' + itemType);
	}
	componentContent = <Component key={'list_item_card_' + key} {...props} />;

	if (data.isCarousel) {
		componentContent = <div key={key + '_car'}>{componentContent}</div>;
	}

	return componentContent;
}

export function getCardPlaceholders(data: IItemCardList): JSX.Element[] {
	const count = Math.max(0, Number(data.nextPageItems) || 0);
	if (!data.items?.length && !data.itemType) {
		return [];
	}
	const item = data.items[0];
	const itemType = (data.itemType ? data.itemType : item.itemType) as CardType;
	return new Array(count).fill(null).map((_, index) => <PlaceholderItemCard itemType={itemType} key={index} />);
}

export function nullCheck(item: any): boolean {
	return typeof item !== 'undefined' && typeof item !== 'undefined' && item !== null;
}

export function offset(el) {
	const rect = el.getBoundingClientRect();
	const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
	const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
	return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
}

function objectsAreSame(x: any, y: any) {
	let objectsAreSame = true;
	for (const propertyName in x) {
		if (x[propertyName] !== y[propertyName]) {
			objectsAreSame = false;
			break;
		}
	}
	return objectsAreSame;
}

export function arraysAreSame(firstArray: any[], secondArray: any[]): boolean {
	let arraysAreSame = true;
	for (const object in firstArray) {
		if (!objectsAreSame(firstArray[object], secondArray[object])) {
			arraysAreSame = false;
			break;
		}
	}
	return arraysAreSame;
}

export function onCloseModalForm(
	dirty: boolean,
	showModal: (modalType: string, modalProps: any) => IModalAction,
	closeModal: () => IModalAction,
	t: (key: string, object?: any) => string,
) {
	if (dirty) {
		showModal('ConfirmationDialog', {
			confirmAction: (confirm: boolean) => {
				closeModal();
				if (confirm) {
					closeModal();
				}
			},
			value: null,
			yesButtonText: t('BSPortal_Common_Yes'),
			noButtonText: t('BSPortal_Common_NoWait'),
			content: (
				<>
					<h1>{i18n.t('BSPortal_Common_ConfirmationDialog_Sure')}</h1>
					<p>{i18n.t('Magazine_Article_ManageArticle_UnsavedChangesModal_Label_AboutUnsavedChanges')}</p>
					<p>{i18n.t('Magazine_Article_ManageArticle_UnsavedChangesModal_Label_LeaveWithoutSave')}</p>
				</>
			),
		});
	} else {
		closeModal();
	}
}

export function truncateText(textToTruncate: string, count: number) {
	let text = textToTruncate;

	if (textToTruncate && count && textToTruncate.length > count) {
		text = textToTruncate.substring(0, count - 3).trim() + '...';
	}

	return text;
}

// https://medium.com/@nidhinkumar/react-js-number-format-and-styling-a1a6e211e629
export function formatDecimal(value: number, numberOfDecimals?: number) {
	if (!numberOfDecimals) {
		numberOfDecimals = 2;
	}

	return new Intl.NumberFormat(moment.locale(), {
		style: 'decimal',
		minimumFractionDigits: numberOfDecimals,
		maximumFractionDigits: numberOfDecimals,
	}).format(value);
}

export function formatInteger(value: number) {
	return new Intl.NumberFormat(moment.locale(), {
		maximumFractionDigits: 0,
	}).format(value);
}

export function formatCurrency(currencyKey: number, value: number): string {
	if (value === undefined || value === null || Number.isNaN(value)) {
		return '';
	}
	if (currencyKey == null || currencyKey == undefined || Number.isNaN(currencyKey)) {
		return formatInteger(value);
	}
	const currency = i18n.t(`DV_Currencies_${currencyKey}`);
	return new Intl.NumberFormat(moment.locale(), {
		style: 'currency',
		currency,
	}).format(value);
}

/**
 * Returns an array of parts. If a part is a string, it is the formatted number.
 * If the part is an object, it is the currency symbol.
 */
export function formatCurrencyToParts(
	currencyKey: number,
	value: number,
): (string | { type: 'currency'; value: string })[] {
	if (currencyKey == null || currencyKey == undefined || value === undefined || value === null) {
		return [];
	}
	const currency = i18n.t(`DV_Currencies_${currencyKey}`);
	const formatted = new Intl.NumberFormat(moment.locale(), {
		style: 'currency',
		currency,
	}).formatToParts(value);

	const parts = [];
	formatted?.forEach?.(
		(el) =>
			el.type == 'currency'
				? parts.push(el)
				: parts[parts.length - 1] && parts[parts.length - 1]?.type != 'currency'
				  ? (parts[parts.length - 1] += String(el.value))
				  : parts.push(el.value),
		[],
	);
	return parts;
}

export function getCurrencySymbol(currencyKey: number) {
	return (formatCurrencyToParts(currencyKey, 0)?.find((p) => typeof p != 'string' && p?.type == 'currency') as any)
		?.value;
}

export function formatPercentage(value: number) {
	return new Intl.NumberFormat(moment.locale(), {
		style: 'percent',
	}).format(value);
}

export function formatDuration(durationInSeconds: number, short = false) {
	const durationInMinutes = Math.round(durationInSeconds / 60) || 0;
	const durationInHours = Math.floor(durationInMinutes / 60) || 0;
	const minutesOnly = durationInMinutes % 60;
	let time = minutesOnly + ' min';
	if (short) {
		time = String(minutesOnly).padStart(2, '0');
	} else if (durationInHours && minutesOnly == 0) {
		time = '';
	}
	if (durationInHours || short) {
		if (short) {
			time = String(durationInHours).padStart(2, '0') + ':' + time;
		} else {
			const delim = time?.length ? ' ' : '';
			time = durationInHours + ' h' + delim + time;
		}
	}
	if (durationInMinutes === 60) {
		time = String(durationInMinutes) + ' min'; // If exactly 60 minutes display "60min" instead of "1h"
	}
	return time;
}

export function renderShippingOptionCountries(
	availableToCountryKeys: number[],
	allCountryKeys: number[],
	europeanUnionCountryKeys,
	t: (key: string, object?: any) => string,
) {
	const availableToCountriesArray: string[] = [];
	if (allCountryKeys.every((x) => availableToCountryKeys.indexOf(x) >= 0)) {
		availableToCountriesArray.push(t(`DV_Countries_${c.WORLDWIDE_OPTION_KEY}`));
	} else if (europeanUnionCountryKeys.every((x) => availableToCountryKeys.indexOf(x) >= 0)) {
		availableToCountriesArray.push(t(`DV_Countries_${c.EUROPEAN_UNION_OPTION_KEY}`));
		availableToCountryKeys.forEach((x) => {
			if (!europeanUnionCountryKeys.find((y) => y === x)) {
				availableToCountriesArray.push(t(`DV_Countries_${x}`));
			}
		});
	} else {
		availableToCountryKeys.map((countryKey: number) => {
			availableToCountriesArray.push(t(`DV_Countries_${countryKey}`));
		});
	}
	return availableToCountriesArray.join();
}

export function setProductPictureUrl(url: string, isCard?: boolean): React.ImgHTMLAttributes<HTMLImageElement> {
	let imageUrl = require('containers/Html/images/productnoimage.svg');
	if (isCard) {
		imageUrl = require('containers/Html/images/productnoimagecard.png');
	}
	if (url && url.length > 0) {
		imageUrl = url;
	}
	return imgSrcAttributes(imageUrl);
}

export function setWardrobeItemPictureUrl(url: string): React.ImgHTMLAttributes<HTMLImageElement> {
	let imageUrl = require('containers/Html/images/wardrobeitemnoimagecard.png');
	if (url && url.length > 0) {
		imageUrl = thumbUrl(url);
	}
	return imgSrcAttributes(imageUrl);
}

export function renderFollowButton(
	isFollowed: boolean,
	followItem: any,
	unfollowItem: any,
	isAuthenticated: () => void,
	signinRedirect: () => void,
	isMyItem?: boolean,
): JSX.Element {
	let followBtnContent: JSX.Element = null;
	if (!isMyItem) {
		if (isFollowed === null) {
			followBtnContent = (
				<div style={{ textAlign: 'center' }}>
					<Spinner />
				</div>
			);
		} else if (!isFollowed) {
			followBtnContent = (
				<Button type="button" size="extrasmall" onClick={() => followItem(isAuthenticated, signinRedirect)}>
					<span>{i18n.t('BSPortal_Common_Follow')}</span>
				</Button>
			);
		} else {
			followBtnContent = (
				<Button type="button" size="extrasmall" onClick={() => unfollowItem(isAuthenticated, signinRedirect)}>
					<span>{i18n.t('BSPortal_Common_Unfollow')}</span>
				</Button>
			);
		}
	}
	return followBtnContent;
}

export function goToCard(dispatch: any, id: string, type: string, target?: string) {
	switch (type) {
		case c.NODE_TYPE_ARTICLE:
			dispatch(getArticleCard(id, target));
			break;

		case c.NODE_TYPE_PRODUCT:
			dispatch(getProductCard(id, target));
			break;

		case c.NODE_TYPE_SHOP:
			dispatch(getShopCard(id, target));
			break;

		case c.NODE_TYPE_USER:
			dispatch(getUserCard(id, target));
			break;

		case c.NODE_TYPE_WARDROBE:
			dispatch(getWardrobeItemCard(id, target));
			break;

		case c.NODE_TYPE_OUTFIT:
			dispatch(getOutfitCard(id, target));
			break;

		default:
			break;
	}
}

export function getRefinementGroupTranslationKey(refinementGroup: ProductRefinementGroupings) {
	let translationString = '';
	switch (refinementGroup) {
		case ProductRefinementGroupings.Brand:
			translationString = 'DV_Brands_';
			break;
		case ProductRefinementGroupings.Color:
			translationString = 'DV_Colors_';
			break;
		case ProductRefinementGroupings.Material:
			translationString = 'DV_Materials_';
			break;
		case ProductRefinementGroupings.Print:
			translationString = 'DV_Prints_';
			break;
		case ProductRefinementGroupings.Size:
			translationString = 'DV_Sizes_';
			break;
		case ProductRefinementGroupings.SkinType:
			translationString = 'DV_SkinTypes_';
			break;
		case ProductRefinementGroupings.Specialities:
			translationString = 'DV_Specialities_';
			break;
		case ProductRefinementGroupings.Format:
			translationString = 'DV_Formats_';
			break;
		case ProductRefinementGroupings.Formulation:
			translationString = 'DV_Formulations_';
			break;
		case ProductRefinementGroupings.Treatment:
			translationString = 'DV_Treatments_';
			break;
		default:
			translationString = null;
	}
	return translationString;
}

export function getMenuItemTypeByPathname(pathname: string) {
	if (pathname.startsWith('/admin/portal')) {
		return MenuItemType.Portal;
	} else if (pathname.startsWith('/admin/magazine')) {
		return MenuItemType.Magazine;
	}

	return null;
}

export function getNextPageCount(itemsPage: IPage | undefined, current: number): number {
	return itemsPage ? Math.max(0, Math.min(itemsPage.itemsPerPage, itemsPage.totalItems - current)) : 0;
}

export function setShoppingBasketExpiryDate() {
	const shoppingBasketExpiryDate = new Date();
	shoppingBasketExpiryDate.setHours(shoppingBasketExpiryDate.getHours() + 1);
	localStorage.setItem('shoppingBasketExpiryDate', shoppingBasketExpiryDate.toString());
}

export function removeShoppingBasketExpiryDate() {
	localStorage.removeItem('shoppingBasketExpiryDate');
}

export function getShoppingBasketExpiryDate() {
	let date = null;
	const dateString = localStorage.getItem('shoppingBasketExpiryDate');
	if (dateString) {
		date = new Date(dateString);
	}
	return date;
}

export function createDomainValueOptions(
	t: (k: string) => string,
	translationPrefix: string | null,
	domainValues?: IDomainValue[],
	sort = true,
) {
	const options: Array<{ key: number; value: string; image: string }> = [];
	const tryTranslate = (dv: IDomainValue) => {
		if (translationPrefix === null) {
			return dv.value;
		}
		const key = translationPrefix + dv.key;
		const result = t(key);
		if (result === key && dv.value) {
			return dv.value;
		}
		return result;
	};
	if (domainValues) {
		domainValues.forEach((dv: IDomainValue) => {
			let image = '';
			// Only country calling codes have countryKey for now
			if (dv?.countryKey) {
				try {
					// image may not exist
					image = require(`../../../wwwroot/images/roundedCountryFlags/${dv.countryKey}.png`);
				} catch (e) {
					console.error(e);
				}
			}
			options.push({
				key: dv.key,
				value: tryTranslate(dv),
				image: dv?.countryKey ? image : '',
			});
		});

		if (sort) {
			options.sort(function (a: IDomainValue, b: IDomainValue) {
				if (a.value < b.value) {
					return -1;
				}
				if (a.value > b.value) {
					return 1;
				}
				return 0;
			});
		}
	}
	return options;
}

export function fullPhoneNumber(
	t: (k: string) => string,
	callingCodeKey?: number | undefined,
	phoneNumber?: string,
): string {
	let fullPhoneNumber = '';
	if (callingCodeKey || callingCodeKey === 0) {
		const callingCodeKeyKey = 'DV_CountryCallingCodes_' + callingCodeKey;
		const callingCodeKeyVal = t(callingCodeKeyKey);
		if (callingCodeKeyKey !== callingCodeKeyVal) {
			fullPhoneNumber += callingCodeKeyVal;
		}
	}
	if (phoneNumber) {
		fullPhoneNumber += phoneNumber;
	}
	return phoneFormatter(fullPhoneNumber);
}

export function createCountriesOptions(t: (k: string) => string, countryDomainValues?: IDomainValue[]) {
	return createDomainValueOptions(t, 'DV_Countries_', countryDomainValues);
}

export function parseEntityId(entityId: string | object, logError = false): IEntityId | null {
	if (typeof entityId === 'object' && entityId && 'id' in entityId && 'type' in entityId) {
		return entityId as IEntityId;
	}
	entityId = String(entityId);
	try {
		const parsed = JSON.parse(entityId);
		if (typeof parsed !== 'object' || !parsed) {
			throw new Error('Incorrect form of entityId ' + entityId);
		}
		if (!('id' in parsed)) {
			throw new Error('Cannot get id from ' + entityId);
		}
		if (!('type' in parsed)) {
			throw new Error('Cannot get type from ' + entityId);
		}
		return parsed as IEntityId;
	} catch (e) {
		if (logError) {
			console.warn(e);
		}
		return null;
	}
}

export function idFromEntityId(entityId: string | { id: string }, logError = false): string {
	return parseEntityId(entityId, logError)?.id || String(entityId);
}

// TODO: add here...
export enum EntityTypes {
	Shop,
	User,
	Office,
}

export function createEntityId(id: string, type: EntityTypes, asString: true): string;
export function createEntityId(id: string, type: EntityTypes, asString: false): IEntityId;
export function createEntityId(id: string, type: EntityTypes, asString: boolean): string | IEntityId {
	let typeString = '';
	switch (type) {
		// TODO: ...and here
		case EntityTypes.Shop:
			typeString = 'Shop';
			break;
		case EntityTypes.User:
			typeString = 'User';
			break;
		case EntityTypes.Office:
			typeString = 'Office';
			break;

		default:
			console.warn('Unsupported entity type', type);
	}
	const entityId = parseEntityId(id, false) || {
		id: String(id),
		type: typeString,
	};
	return asString ? JSON.stringify(entityId) : entityId;
}

export function getGuidFromString(value: string) {
	const regex = /(\{){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\}){0,1}/gm;
	if (value && value.match(regex)) {
		return value.match(regex)[0];
	}
	return '';
}

/**
 * Convertrs ISO date format to shortened month name, day of month, year localized format
 * @param ISODate
 */
export function dateParser(ISODate: string | Date): string {
	return moment(ISODate).format('ll');
}

export function dayMonthFormatter(date): string {
	if (moment.locale() === 'en') {
		return moment(date).format('MMM D');
	} else {
		return moment(date).format('D MMM');
	}
}

/**
 * Split phone number from "+38641234567" to "+386 41 234 567"
 * @param phoneNumber
 */
export function phoneFormatter(phoneNumber: string): string {
	return phoneNumber?.replace(/(\+\d{3})\D?(\d{2})\D?(\d{3})\D?(\d{3})/, '$1 $2 $3 $4');
}

export function shouldComponentUpdate<IProps>(prevProps: IProps, nextProps: IProps): boolean;
export function shouldComponentUpdate<IProps, IState>(
	prevProps: IProps,
	nextProps: IProps,
	prevState: IState,
	nextState: IState,
): boolean;
export function shouldComponentUpdate<IProps, IState>(
	prevProps: IProps,
	nextProps: IProps,
	prevState?: IState,
	nextState?: IState,
): boolean {
	if (!prevProps && !nextProps) {
		return false;
	}
	if (Boolean(prevProps) !== Boolean(nextProps)) {
		return true;
	}
	const allPropsKeys = [...Object.keys(nextProps), ...Object.keys(prevProps)].filter(
		(v, i, all) => all.indexOf(v) === i,
	);
	for (const i of allPropsKeys) {
		const old = prevProps[i];
		const next = nextProps[i];
		if (old !== next && JSON.stringify(old) !== JSON.stringify(next)) {
			return true;
		}
	}
	if (!prevState && !nextState) {
		return false;
	}
	if (Boolean(prevState) !== Boolean(nextState)) {
		return true;
	}
	const allStateKeys = [...Object.keys(nextState), ...Object.keys(prevState)].filter(
		(v, i, all) => all.indexOf(v) === i,
	);
	for (const i of allStateKeys) {
		const old = prevState[i];
		const next = nextState[i];
		if (old !== next && JSON.stringify(old) !== JSON.stringify(next)) {
			return true;
		}
	}
	return false;
}

function isUploadedImage(imageUrl: string): boolean {
	if (!imageUrl || typeof imageUrl !== 'string') {
		return false;
	}
	if (imageUrl.indexOf('/uploads/img/') === -1) {
		return false;
	}
	return true;
}

export function thumbUrl(imageUrl: string, name = c.DEFAULT_CROP_NAME): string {
	if (!isUploadedImage(imageUrl)) {
		return imageUrl;
	}
	const imageParts = imageUrl.split('/');
	let lastPart = imageParts.pop();
	if (lastPart.indexOf(name) !== 0) {
		lastPart = name + lastPart;
	}
	imageParts.push(lastPart);
	return imageParts.join('/');
}

export function bigImageUrl(imageUrl: string): string {
	if (!isUploadedImage(imageUrl)) {
		return imageUrl;
	}
	const name = c.DEFAULT_CROP_NAME;
	const imageParts = imageUrl.split('/');
	let lastPart = imageParts.pop();
	if (lastPart.indexOf(name) === 0) {
		lastPart = lastPart.substring(name.length);
	}
	imageParts.push(lastPart);
	return imageParts.join('/');
}

export function imgSrcAttributes(src: string): React.ImgHTMLAttributes<HTMLImageElement> {
	if (!isUploadedImage(src)) {
		return {
			src,
		};
	}
	return {
		src: thumbUrl(src),
		// srcSet: thumbUrl(src) + ' 580w, ' + bigImageUrl(src),
		onError: ({ currentTarget }) => {
			currentTarget.onerror = null;
			if (bigImageUrl(src) !== currentTarget.src) {
				currentTarget.src = bigImageUrl(src);
			}
		},
	};
}

export const timePickerProps = (minTime: Date, maxTime: Date): Partial<ReactDatePickerProps> => ({
	showTimeSelect: true,
	showTimeSelectOnly: true,
	timeIntervals: c.MIN_TIME_INTERVAL_MINUTES,
	dateFormat: 'HH:mm',
	timeFormat: 'HH:mm',
	shouldCloseOnSelect: true,
	minTime: minTime,
	maxTime: maxTime,
});

type ValidationFunction = (value: Date) => string | undefined;

const timeValidationFnCacheMin: { [key: string]: ValidationFunction } = {};
const timeValidationFnCacheMax: { [key: string]: ValidationFunction } = {};

export function createTimeValidationFuntion(
	side: 'min' | 'max',
	timeDate: Date,
	t: ITranslateProps['t'],
	timezone: string,
): ValidationFunction {
	const timeString = momentWithTimezone(timezone, timeDate).format('YYYY-MM-DD HH:mm');
	const timeValidationFnCache = side === 'min' ? timeValidationFnCacheMin : timeValidationFnCacheMax;
	if (!(timeString in timeValidationFnCache)) {
		timeValidationFnCache[timeString] = (value) => {
			if (!value) {
				return undefined;
			}
			const val = momentWithTimezone(timezone, value).format('YYYY-MM-DD HH:mm');
			if (side === 'min') {
				if (val < timeString) {
					return t('Validation_Client_Field_MinValue', { min: momentWithTimezone(timezone, timeDate).format('HH:mm') });
				}
			} else if (side === 'max') {
				if (val > timeString) {
					return t('Validation_Client_Field_MaxValue', { min: momentWithTimezone(timezone, timeDate).format('HH:mm') });
				}
			}
			return undefined;
		};
	}
	return timeValidationFnCache[timeString];
}

export const getIdFromObject = <T extends { id?: string }>(item: string | T): string =>
	typeof item === 'string' ? item : item.id;

export const uniq = <T extends string | number>(a: T[]): T[] => a.filter((v, i, all) => all.indexOf(v) === i);

const normalizeObjectPropertiesNames = <T extends {}>(t: T, fnName: 'toLowerCase' | 'toUpperCase'): T => {
	if (typeof t !== 'object' || !t || Array.isArray(t)) {
		return t;
	}
	const obj = {};
	for (const propName in t) {
		const propNameLower = propName.slice(0, 1)[fnName]() + propName.slice(1);
		obj[propNameLower] = t[propName];
	}
	return obj as T;
};

export const lowerFirstObjectPropertiesNames = <T extends {}>(t: T): T => {
	return normalizeObjectPropertiesNames(t, 'toLowerCase');
};
export const upperFirstObjectPropertiesNames = <T extends {}>(t: T): T => {
	return normalizeObjectPropertiesNames(t, 'toUpperCase');
};

export const isValidUrl = (value: string) => {
	let url;

	try {
		url = new URL(value);
	} catch (_) {
		return false;
	}

	return url.protocol === 'http:' || url.protocol === 'https:';
};

/////////////////////////////////////
// URL search params manipulation
/////////////////////////////////////
export const addURLSearchParams = (name: string, value: string, history?: any) => {
	if (typeof window !== 'undefined') {
		const url = new URL(window.location.href);

		// Param exists - remove it first
		if (url.searchParams.get(name)) {
			url.searchParams.delete(name);
		}
		// Param doesn't exist
		url.searchParams.append(name, value);

		if (history) {
			history.push({
				pathname: url.pathname,
				search: url.search,
			});
		} else {
			window.history.replaceState('', '', url.href);
		}
	}
};

export const addMultipleURLSearchParams = (data: { name: string; value: string }[], history?: any) => {
	if (typeof window !== 'undefined') {
		const url = new URL(window.location.href);

		data.forEach((datum) => {
			// Param exists - remove it first
			if (url.searchParams.get(datum.name)) {
				url.searchParams.delete(datum.name);
			}
			if (datum?.value) {
				url.searchParams.append(datum.name, datum.value);
			}
		});

		if (history) {
			history.push({
				pathname: url.pathname,
				search: url.search,
			});
		} else {
			window.history.replaceState('', '', url.href);
		}
	}
};

export const deleteURLSearchParams = (names: string[], history?: any) => {
	if (typeof window !== 'undefined') {
		const url = new URL(window.location.href);

		names.forEach((x) => {
			// Check if exists before trying to delete
			if (url.searchParams.get(x)) {
				url.searchParams.delete(x);
			}
		});

		// if component depends on withRouter.location, we need to pass
		// browserHistory from "react-router" as history prop, else withRouter.location
		// wont update on url change
		if (history) {
			history.push({
				pathname: url.pathname,
				search: url.search,
			});
		} else {
			window.history.replaceState('', '', url.href);
		}
	}
};

export const readURLSearchParams = (name: string): string | null => {
	if (typeof window !== 'undefined') {
		const url = new URL(window.location.href);

		return url.searchParams.get(name);
	}
};

export const readMultipleURLSearchParams = (names: string[]): { [key: string]: string } | undefined => {
	if (typeof window !== 'undefined') {
		const url = new URL(window.location.href);

		const out = {};
		names.forEach((name) => {
			out[name] = url.searchParams.get(name);
		});
		return out;
	}
	return {};
};

/**
 * Submits form element
 * @param form
 */
export const submitForm = (form: HTMLFormElement) => {
	// RequestForm does not work on safari
	if (form?.requestSubmit) {
		form.requestSubmit();
	} else {
		form?.dispatchEvent(new Event('submit'));
	}
};
/////////////////////////////////////
export const generateSlug = (value: string): string => {
	let generatedSlug: string = value
		.toLowerCase()
		.trim()
		.replace(/đ|Đ/g, 'd')
		.replace(/ć|Ć|č|Č/g, 'c')
		.replace(/š|Š/g, 's')
		.replace(/ž|Ž/g, 'z')
		.replace(/đ|Đ/g, 'd')
		.replace(/[^a-zA-Z \-0-9]/g, '')
		.replace(/-+/g, '-')
		.replace(/\s\s+/g, ' ')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-')
		.replace(/(^-+|-+$)/g, '');
	if (generatedSlug[generatedSlug.length - 1] === '-') {
		generatedSlug = generatedSlug.slice(0, -1);
	}
	return generatedSlug;
};

type ProfileTab = 'default' | 'listings' | 'appointments' | 'about';

export const getProfileTabUrl = (tab: ProfileTab) => {
	switch (tab) {
		case 'listings':
			return '?tab=listings';
		case 'appointments':
			return '?tab=appointments';
		case 'about':
			return '?tab=about';
		default:
			return '?tab=listings';
	}
};

type CustomerTab = 'default' | 'overview' | 'bio' | 'appointments';

export const getCustomerTabUrl = (tab: CustomerTab) => {
	switch (tab) {
		case 'overview':
			return '?tab=overview';
		case 'bio':
			return '?tab=bio';
		case 'appointments':
			return '?tab=appointments';
		case 'default':
			return '?tab=overview';
	}
};

export const setOtherZone = (date: Date | string, timezone: string) => {
	const dateWithoutZone = moment(date).format('YYYY-MM-DDTHH:mm:ss.SSS');
	const otherZone = momentWithTimezone(timezone, date).format('Z');
	const dateWithOtherZone = [dateWithoutZone, otherZone].join('');

	return new Date(dateWithOtherZone).toISOString();
};

declare global {
	interface Document {
		documentMode?: any;
	}
}

export const detectIncompatibleBrowsers = () => {
	// https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browsers#:~:text=You%20can%20try%20the%20following%20way%20to%20check%20the%20browser%3A
	// Embedded browsers, Firefox iOS, Edge and Opera iOS cause jumpy scroll, so we need to detect them and apply a workaround (BUG-8721)

	if (navigator.userAgent.indexOf('FxiOS') != -1) {
		// Firefox iOS
		return true;
	} else if (navigator.userAgent.indexOf('Edg') != -1) {
		// Edge
		return true;
	} else if (navigator.userAgent.indexOf('Safari') != -1) {
		// Safari and iOS Safari
		// opera iOS is recognized as Safari and the other way around, so Opera iOS still has jumpy scroll
		return false;
	} else if (navigator.userAgent.indexOf('OPiOS') != -1) {
		// Opera iOS
		return true;
	} else if (navigator.userAgent.indexOf('Chrome') != -1) {
		// Chrome
		return false;
	} else if (navigator.userAgent.indexOf('Firefox') != -1) {
		// Firefox
		return false;
	} else if (navigator.userAgent.indexOf('MSIE') != -1 || !!document.documentMode == true) {
		// IE
		return false;
	} else {
		// Unknown/embeded browser
		return true;
	}
};

export const createLanguageDropdownValues = (languageDomainValues: ILanguage[], t: ITranslateProps['t']) => {
	const languages = [];

	if (languageDomainValues) {
		languageDomainValues.forEach((language, i) => {
			if (i >= 4) {
				return;
			}
			const valueKey = `DV_Languages_${language.key}`;
			let value = t(valueKey);
			if (value === valueKey) {
				if (language.value) {
					value = language.value;
				} else if (language.culture) {
					value = language.culture;
				}
			}
			languages.push({
				key: language.key,
				value,
			});
		});

		languages.sort(function (a: IDomainValue, b: IDomainValue) {
			if (a.value < b.value) {
				return -1;
			}
			if (a.value > b.value) {
				return 1;
			}
			return 0;
		});
	}

	return languages;
};
